
import { type Investment } from '@resolute/schema';
import { Badge } from '@resolute/ui';
import { formatCurrency, formatINRCompact, projectInvestmentReturn } from '@resolute/utils';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// Default assumptions for the at-a-glance projection (users can model their own
// in the rental-yield tool). Conservative, clearly-labelled indicative figures.
const DEFAULT_APPRECIATION_PCT = 6;
const DEFAULT_HOLD_YEARS = 5;

/** Yield / cap-rate summary + indicative ROI projection for pre-leased assets. */
export function InvestmentPanel({ investment, price }: { investment: Investment; price: number }) {
  const metrics = [
    investment.grossYieldPct != null && {
      label: 'Gross yield',
      value: `${investment.grossYieldPct.toFixed(2)}%`,
    },
    investment.netYieldPct != null && {
      label: 'Net yield',
      value: `${investment.netYieldPct.toFixed(2)}%`,
    },
    investment.capRatePct != null && {
      label: 'Cap rate',
      value: `${investment.capRatePct.toFixed(2)}%`,
    },
    investment.monthlyRent != null && {
      label: 'Monthly rent',
      value: formatCurrency(investment.monthlyRent),
    },
  ].filter(Boolean) as { label: string; value: string }[];

  const projection =
    price > 0 && investment.monthlyRent != null
      ? projectInvestmentReturn({
          price,
          monthlyRent: investment.monthlyRent,
          annualCost: investment.annualCost,
          rentEscalationPct: investment.escalationPct ?? 5,
          appreciationPct: DEFAULT_APPRECIATION_PCT,
          holdingYears: DEFAULT_HOLD_YEARS,
        })
      : null;

  return (
    <section
      aria-labelledby="investment-heading"
      className="rounded-lg border border-border bg-accent/40 p-5"
    >
      <div className="flex items-center gap-2">
        <h2 id="investment-heading" className="text-lg font-semibold">
          Investment snapshot
        </h2>
        <Badge variant="success">Pre-leased</Badge>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {metrics.map((m) => (
          <div key={m.label}>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">{m.label}</dt>
            <dd className="mt-1 text-xl font-semibold text-primary">{m.value}</dd>
          </div>
        ))}
      </dl>

      {(investment.tenant || investment.leaseTermMonths) && (
        <p className="mt-4 text-sm text-muted-foreground">
          {investment.tenant && (
            <>
              Tenant: <span className="text-foreground">{investment.tenant}</span>.{' '}
            </>
          )}
          {investment.leaseTermMonths != null && (
            <>
              Lease term:{' '}
              <span className="text-foreground">
                {Math.round(investment.leaseTermMonths / 12)} years
              </span>
              {investment.escalationPct != null && ` · ${investment.escalationPct}% escalation`}.
            </>
          )}
        </p>
      )}

      {projection && (
        <div className="mt-4 rounded-md border border-border bg-background/60 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-medium">
              {DEFAULT_HOLD_YEARS}-year projected return
              <span className="ml-1 font-normal text-muted-foreground">
                (at {DEFAULT_APPRECIATION_PCT}% appreciation)
              </span>
            </p>
            <p className="text-lg font-semibold text-success">
              {formatINRCompact(projection.totalReturn)}{' '}
              <span className="text-sm font-normal text-muted-foreground">
                · {projection.annualizedReturnPct.toFixed(1)}% p.a.
              </span>
            </p>
          </div>
          <Link
            to="/calculators/rental-yield"
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            Model your own assumptions <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
        </div>
      )}

      <p className="mt-2 text-xs text-muted-foreground">
        Indicative figures based on listing data — verify before investing.
      </p>
    </section>
  );
}
